<template>
  <tr class="data-row">
    <td class="name-cell" :title="row.client_name">
      <div class="name-wrap">
        <template v-if="row.type === 'opportunity'">
          <a href="#" @click.prevent="navigateToOpportunity" class="opp-name">{{ row.name }}</a>
          <div v-if="row.client_name" class="opp-client">{{ row.client_name }}</div>
        </template>
        <template v-else-if="isRecurringType">
          <span class="opp-name">{{ row.name }}</span>
          <div v-if="row.client_name" class="opp-client">{{ row.client_name }}</div>
        </template>
        <template v-else>
          <span v-if="!editingName" @click="startEditName" class="opp-name tentative-name">{{ row.name }}</span>
          <input
            v-else
            ref="nameInput"
            v-model="nameValue"
            type="text"
            class="name-input"
            @blur="saveName"
            @keyup.enter="saveName"
            @keyup.escape="cancelEditName"
          />
        </template>
      </div>
    </td>
    <forecast-cell
      v-for="(month, idx) in months"
      :key="month"
      :type="row.type"
      :invoices="getInvoicesForMonth(month)"
      :month-data="getMonthData(month)"
      :month="month"
      :row-status="row.row_status"
      :is-quarter-start="idx > 0 && idx % 3 === 0"
      :compact="compact"
      @update-month="onUpdateMonth"
      @remove-month="onRemoveMonth"
      @navigate-opportunity="navigateToOpportunity"
    />
    <td class="total-cell">
      {{ formatCents(rowTotal) }}
    </td>
    <td class="action-cell">
      <button
        v-if="row.type === 'tentative'"
        class="delete-btn"
        @click="onDelete"
        title="Delete row"
      >
        <i class="far fa-trash-alt"></i>
      </button>
    </td>
  </tr>
</template>

<script>
import ForecastCell from './ForecastCell.vue'

export default {
  components: { ForecastCell },

  props: {
    row: { type: Object, required: true },
    months: { type: Array, required: true },
    compact: { type: Boolean, default: true }
  },

  data() {
    return {
      editingName: false,
      nameValue: ''
    }
  },

  computed: {
    isRecurringType() {
      return ['sla', 'retainer', 'budget', 'devops', 'hosting', 'unknown'].includes(this.row.type)
    },

    // FY total is computed on the backend (ForecastSummaryService::rowTotal)
    rowTotal() {
      return this.row.fy_total || 0
    }
  },

  methods: {
    formatCents(cents) {
      const sign = cents < 0 ? '-' : ''
      if (this.compact) {
        const dollars = Math.abs(cents / 100)
        if (dollars >= 1000000) return sign + '$' + (dollars / 1000000).toFixed(1) + 'M'
        if (dollars >= 1000) return sign + '$' + Math.round(dollars / 1000) + 'K'
        if (dollars === 0) return '$0'
        return sign + '$' + Math.round(dollars)
      }
      return sign + '$' + this.$options.filters.currency(Math.abs(cents / 100))
    },

    formatCurrency(cents) {
      const dollars = cents / 100
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(dollars)
    },

    getInvoicesForMonth(month) {
      if (this.isRecurringType) {
        const m = this.row.months && this.row.months[month]
        return (m && m.invoices) || []
      }
      return (this.row.invoices && this.row.invoices[month]) || []
    },

    getMonthData(month) {
      if (this.isRecurringType) {
        const m = this.row.months && this.row.months[month]
        return (m && m.source === 'expected') ? m : null
      }
      return (this.row.months && this.row.months[month]) || null
    },

    navigateToOpportunity() {
      if (this.row.opportunity_id) {
        this.$router.push({ name: 'opportunities.overview', params: { opportunity_id: this.row.opportunity_id } })
      }
    },

    startEditName() {
      this.editingName = true
      this.nameValue = this.row.name
      this.$nextTick(() => {
        if (this.$refs.nameInput) this.$refs.nameInput.focus()
      })
    },

    saveName() {
      this.editingName = false
      if (this.nameValue && this.nameValue !== this.row.name) {
        this.$emit('update-name', { entryId: this.row.forecast_entry_id, name: this.nameValue })
      }
    },

    cancelEditName() {
      this.editingName = false
    },

    onUpdateMonth({ month, amount }) {
      this.$emit('update-month', { entryId: this.row.forecast_entry_id, month, amount })
    },

    onRemoveMonth(monthId) {
      this.$emit('remove-month', { entryId: this.row.forecast_entry_id, monthId })
    },

    onDelete() {
      if (confirm('Are you sure you want to delete this tentative row?')) {
        this.$emit('delete-row', this.row.forecast_entry_id)
      }
    }
  }
}
</script>

<style scoped>
.data-row td {
  border-bottom: 1px solid #f5f6fa;
  height: 44px;
}
.data-row:hover td {
  background: #f7f8fe !important;
}

/* Name cell */
.name-cell {
  text-align: left;
  padding: 8px 20px;
  position: sticky;
  left: 0;
  z-index: 2;
  background: #fff;
  border-right: 1px solid #e8eaf2;
  min-width: 220px;
  max-width: 280px;
}
.data-row:hover .name-cell {
  background: #f7f8fe !important;
}
.name-wrap {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.opp-name {
  font-weight: 600;
  color: #1e1e2d;
  font-size: 0.82rem;
  text-decoration: none;
}
.opp-name:hover {
  color: #4057df;
}
.tentative-name {
  color: #8b8fa7;
  cursor: pointer;
}
.tentative-name:hover {
  color: #4057df;
}
.opp-client {
  font-size: 0.68rem;
  color: #a0a4b8;
  font-weight: 400;
}
.name-input {
  width: 100%;
  font-size: 0.82rem;
  padding: 2px 6px;
  border: 1px solid #4057df;
  border-radius: 4px;
  outline: none;
}

/* Total cell */
.total-cell {
  font-weight: 700;
  color: #1e1e2d;
  font-size: 0.82rem;
  text-align: right;
  padding-right: 16px;
  white-space: nowrap;
}

/* Action cell */
.action-cell {
  width: 40px;
  text-align: center;
  vertical-align: middle;
}
.delete-btn {
  border: none;
  background: transparent;
  color: #c5cae0;
  cursor: pointer;
  padding: 4px;
  font-size: 0.8rem;
  transition: color 0.15s;
}
.delete-btn:hover {
  color: #ff6b6b;
}
</style>
